import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { formatDateTime } from "../utils/formatDateTime";
import PaginationControls from "../components/ui/PaginationControls";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { AddButton } from "../components/ui/Button";
import { workflowApi } from "../Apis/Api";
import { apiCall } from "../Apis/apiCall";
import { toast } from "react-toastify";
import InfoModal from "../components/ui/InfoeModal";

const IncentiveAssignment = () => {
  const navigate = useNavigate();
  const [incentiveAssignments, setIncentiveAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    fetchIncentiveAssignments();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  const fetchIncentiveAssignments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await workflowApi.getAllIncentives({
        page: pagination.currentPage,
        limit: pagination.perPage,
        search: debouncedSearch,
        sort_by: sortConfig.field || "assignment_id",
        sort_order: sortConfig.direction,
      });

      if (response.success) {
        let filteredData = response.data || [];

        // Apply status filter if needed (client-side filtering)
        if (filters.status) {
          filteredData = filteredData.filter((assignment) => {
            const status = assignment.is_active ? "Active" : "Inactive";
            return status === filters.status;
          });
        }

        // Format data for display
        const formattedData = filteredData.map((assignment) => ({
          assignment_id: assignment.assignment_id || "-",
          plan_id: assignment.plan_id || "-",
          plan_name: assignment.plan_name || "-",
          branch_id: assignment.branch_id || "-",
          branch_name: assignment.branch_name || "-",
          location_id: assignment.location_id || "-",
          location_name: assignment.location_name || "-",
          workflow_id: assignment.workflow_id || "-",
          workflow_name: assignment.name || "-",
          is_active: assignment.is_active ? "Active" : "Inactive",
          start_datetime: assignment.start_datetime || "-",
          end_datetime: assignment.end_datetime || "-",
          display_start_datetime: assignment.start_datetime
            ? formatDateTime(assignment.start_datetime)
            : "-",
          display_end_datetime: assignment.end_datetime
            ? formatDateTime(assignment.end_datetime)
            : "-",
        }));

        setIncentiveAssignments(formattedData);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total || filteredData.length,
        }));

        setError(null);
      } else {
        throw new Error(
          response.message || "Failed to fetch incentive assignments"
        );
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to fetch incentive assignments.");
      setIncentiveAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (assignment) => {
    navigate("", { state: { assignmentData: assignment } });
  };

  const handleViewDetails = (assignment) => {
    navigate("/view-incentive-assignment", {
      state: { assignmentData: assignment },
    });
  };

  const handleDeleteClick = (assignmentId, planName) => {
    console.log("Assignment to delete:", { id: assignmentId, name: planName });
    setAssignmentToDelete({ id: assignmentId, name: planName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (assignmentToDelete && assignmentToDelete.id) {
      setLoading(true);
      setError(null);

      try {
        const response = await apiCall(
          "delete",
          `/delete-incentive-assignment/${assignmentToDelete.id}`
        );
       
         setShowDeleteModal(false);
         setAssignmentToDelete(null);
         toast.success(response.data.message);
         await fetchIncentiveAssignments();
      } catch (err) {
        console.error("Delete error:", err);
        toast.error(err.message);
        setShowDeleteModal(false);
        setAssignmentToDelete(null);
      } finally {
        setLoading(false);
      }
    } else {
      setError("No valid assignment selected for deletion.");
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setAssignmentToDelete(null);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchIncentiveAssignments} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by Plan Name, Branch, Location, or Workflow"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search incentive assignments"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2"> {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Incentive Assignment List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Incentive Assignment"
              permissionKey="add_incentive_assignment"
              onClick={() => navigate("/add-incentive-assgiment")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Incentive Assignment List" />

      {/* Show error alert if there's an error but still have data */}
      {error && incentiveAssignments.length > 0 && (
        <div className="mb-3">
          <div className="alert alert-warning" role="alert">
            <strong>Warning:</strong> {error}
          </div>
        </div>
      )}

      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {incentiveAssignments.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No incentive assignments match your search or filter."
                  : "No incentive assignments available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>

                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("plan_name")}
                    role="button"
                    aria-label={`Sort by plan name, currently sorted ${
                      sortConfig.field === "plan_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Plan Name
                      <SortIcons field="plan_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("branch_name")}
                    role="button"
                  >
                    <div className="d-flex align-items-center">
                      Branch
                      <SortIcons field="branch_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("location_name")}
                    role="button"
                  >
                    <div className="d-flex align-items-center">
                      Location
                      <SortIcons
                        field="location_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("workflow_name")}
                    role="button"
                  >
                    <div className="d-flex align-items-center">
                      Workflow
                      <SortIcons
                        field="workflow_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                  >
                    <div className="d-flex">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("start_datetime")}
                    role="button"
                  >
                    <div className="d-flex align-items-center">
                      Start Date
                      <SortIcons
                        field="start_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("end_datetime")}
                    role="button"
                  >
                    <div className="d-flex align-items-center">
                      End Date
                      <SortIcons field="end_datetime" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {incentiveAssignments.map((assignment, index) => (
                  <tr key={assignment.assignment_id || `row-${index}`}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>

                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {assignment.plan_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {assignment.branch_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {assignment.location_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {assignment.workflow_name}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={assignment.is_active} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {assignment.display_start_datetime}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {assignment.display_end_datetime}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("delete_incentive_assignment") &&
                          assignment.assignment_id && (
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteClick(
                                  assignment.assignment_id,
                                  assignment.plan_name
                                )
                              }
                              className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title={`Delete ${assignment.plan_name}`}
                              aria-label={`Delete incentive assignment ${assignment.plan_name}`}
                            >
                              <Icon
                                icon="fluent:delete-24-regular"
                                className="menu-icon"
                              />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Delete Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none",backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>
                  {assignmentToDelete?.name || "this incentive assignment"}
                </strong>
                ?
              </p>
              <p className="text-neutral-900 small">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                aria-label={`Confirm delete incentive assignment ${
                  assignmentToDelete?.name || ""
                }`}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}

            {/* NEW: Reusable Info Modal */}
      <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Incentive Assignment List - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This page shows exactly <strong>which Incentive Plan is assigned to which Branch, Location & Workflow</strong>. 
    You can view all active assignments, create new ones, and remove any assignment instantly.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">What You See in the List</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Incentive Plan Name</strong></li>
    <li className="list-group-item"><strong>Branch</strong> – Where it is active</li>
    <li className="list-group-item"><strong>Location</strong> – Specific location(s) inside the branch</li>
    <li className="list-group-item"><strong>Workflow</strong> – Which workflow triggers this incentive</li>
    <li className="list-group-item"><strong>Assigned Date</strong></li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Add New Assignment – Step by Step</h6>
  <p className="small text-neutral-600 mb-2">Click the "Incentive Assignment" button → Fill these fields:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Select Branch</strong></li>
    <li className="list-group-item"><strong>Select Location(s)</strong> – One or multiple from the chosen branch</li>
    <li className="list-group-item"><strong>Select Workflow</strong></li>
    <li className="list-group-item"><strong>Select Incentive Plan</strong></li>
    <li className="list-group-item">Click “Assign” → Instantly active!</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Action Button (Per Assignment)</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Delete (Red Trash)</strong><br/>
      Permanently removes the assignment<br/>
      <small className="text-danger-600">Incentive immediately stops for that Branch/Location/Workflow</small>
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Note:</strong><br/>
    • No “Edit” button – just delete old assignment and create a new one with updated settings<br/>
    • One plan can be assigned to multiple branches, locations & workflows<br/>
    • Changes take effect immediately across the system
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Want to change anything? Delete + Re-assign with new settings → Takes 10 seconds!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Correct assignments = Staff earn incentives on time → More motivation → Higher performance → More revenue!
  </p>
</InfoModal>
    </>
  );
};

export default IncentiveAssignment;